import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Save, Tag } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface TagsSectionProps {
  courseId?: string;
  initialData: any;
}

export default function TagsSection({
  courseId,
  initialData,
}: TagsSectionProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(
    initialData?.selectedTags || [],
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []);

  // Update selected tags when initialData changes
  useEffect(() => {
    if (initialData?.selectedTags) {
      setSelectedTags(initialData.selectedTags);
    }
  }, [initialData]);

  const checkForChanges = useCallback(() => {
    const initialTags = initialData?.selectedTags || [];
    const hasChanges =
      selectedTags.length !== initialTags.length ||
      selectedTags.some((tag) => !initialTags.includes(tag)) ||
      initialTags.some((tag) => !selectedTags.includes(tag));
    setHasUnsavedChanges(hasChanges);
  }, [selectedTags, initialData]);

  useEffect(() => {
    checkForChanges();
  }, [selectedTags, checkForChanges]);

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from("course_tags")
        .select("*")
        .order("name");

      if (error) throw error;
      setTags(data || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
      setError("Failed to load tags");
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!courseId) return;

      // Delete existing tag mappings
      await supabase.from("course_tags_map").delete().eq("course_id", courseId);

      // Insert new tag mappings if there are any
      if (selectedTags.length > 0) {
        const tagMappings = selectedTags.map((tagId) => ({
          course_id: courseId,
          tag_id: tagId,
        }));

        const { error } = await supabase
          .from("course_tags_map")
          .insert(tagMappings);

        if (error) throw error;
      }

      // Update initialData reference after successful save
      Object.assign(initialData, { selectedTags });
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving tags:", err);
      setError(err instanceof Error ? err.message : "Failed to save tags");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">Tags</h2>
          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
              hasUnsavedChanges
                ? "text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                : "text-gray-500 bg-gray-100"
            } disabled:opacity-50 transition-colors duration-200`}
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Saving..." : "Save Tags"}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Course Tags
          </label>
          <div className="mt-2 space-y-2">
            {tags.map((tag) => (
              <label
                key={tag.id}
                className="inline-flex items-center mr-4 mb-2"
              >
                <input
                  type="checkbox"
                  checked={selectedTags.includes(tag.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTags([...selectedTags, tag.id]);
                    } else {
                      setSelectedTags(
                        selectedTags.filter((id) => id !== tag.id),
                      );
                    }
                  }}
                  className="rounded border-gray-300 text-[#F98B3D] focus:ring-[#F98B3D]"
                />
                <span className="ml-2 text-sm text-gray-700">{tag.name}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Select relevant tags to help students discover your course.
          </p>
        </div>

        {/* Quick tag creation */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-900">Need a new tag?</h3>
          <p className="mt-1 text-sm text-gray-500">
            Visit the{" "}
            <Link
              to="/admin/course-tags"
              className="text-[#F98B3D] hover:text-[#e07a2c]"
            >
              Tags Management
            </Link>{" "}
            page to create or edit tags.
          </p>
        </div>
      </div>
    </div>
  );
}
